import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";

import { Input } from "@digital-services/ui";

import { UserState } from "@organization/models/EssentialUser";
import { OrganizationRole } from "@organization/models/OrganizationRole";

import { useInput } from "@hooks/useInput";
import { isEmailAddressValid } from "@utils/validators/EmailAddressValidator";
import { OnInputChangeFunctor } from "@utils/validators/models/ValidationResult";

import { useInviteFormContext } from "../../context";
import { useGetUserByEmail } from "../../services/use-get-user-by-email";
import { useGetUserOfCurrentOrganization } from "../../services/use-get-user-of-current-organization";
import { EmailInputAdornment } from "../EmailInputAdornment";

interface Props {
  id: string;
  label: string;
  initialEmailValue: string | undefined;
  onEmailChange?: OnInputChangeFunctor;
}

// Note that this component is used in a few places, and NOT all have access to the same context
// That is why is rather better to receive user email address as `initialEmailValue` as component param.
export const UserEmailExtended = (props: Props) => {
  const { id, initialEmailValue, label, onEmailChange } = props;

  const { t } = useTranslation();

  const { t: tInvite } = useTranslation(undefined, {
    keyPrefix: "organization.users.invite"
  });

  const infoWhenInviteExpired = tInvite("validation.info-when-invite-expired");
  const infoWhenUserIsOrganizationAdmin = tInvite(
    "validation.info-when-user-is-organization-admin"
  );
  const infoWhenUserIsOrganizationUser = tInvite(
    "validation.info-when-user-is-organization-user"
  );

  const errorLabel = isEmailAddressValid(
    t("validation.string.invalid-email", {
      fieldName: label
    })
  );

  const { isValid, valueChangeHandler, errorMessage } = useInput(
    [errorLabel],
    initialEmailValue
  );

  const [email, setEmail] = useState<string | undefined>(initialEmailValue);

  const {
    setContextDisplayName,
    setContextDisableSubmit,
    setContextReInviteMode
  } = useInviteFormContext();

  const [debouncedEmailValue] = useDebounce(isValid ? email : null, 2000);

  // We need to be 200% that when email value is NOT valid from Frontend then NO reason to send request to backend.
  // According to how how `swr` works, `null` value guarantees us NO request sent.

  const [formState, setFormState] = useState({
    helperInfo: "",
    pseudoError: false
  });

  const {
    isLoading: loadingDataFromUserByEmail,
    data: dataFromUserByEmail,
    error: errorFromUserByEmail
  } = useGetUserByEmail(debouncedEmailValue || null);

  const {
    isLoading: loadingDataFromCurrentOrganization,
    data: dataFromUserByOrg,
    error: errorFromUserByOrg
  } = useGetUserOfCurrentOrganization(
    dataFromUserByEmail ? dataFromUserByEmail.userId : null
  );

  useEffect(() => {
    if (dataFromUserByEmail?.displayName) {
      setContextDisplayName(dataFromUserByEmail?.displayName);
    }

    switch (dataFromUserByEmail?.state) {
      case UserState.Inactive:
      case UserState.InvitationExpired:
        // Use Case - real re-invite of existed user
        setFormState({ helperInfo: infoWhenInviteExpired, pseudoError: false });
        setContextDisableSubmit(false);
        setContextReInviteMode(true);
        break;

      case UserState.Invited:
      case UserState.Active:
        // It covers users with "invitationExpiration": null (state = Active)
        // and also user with FUTURE "invitationExpiration": "2026-05-27T13:03:21.8452171+02:00" (state = Invited or Active)

        if (errorFromUserByOrg) {
          // statusCode = 500
          // "Failed to get the user details for email 'abc@example.com'"
          // statusCode = 500
          // "User with id '0e11ec9e-9155-4f06-b780-eabad10ce294' could not be found in organization with id 'b535b4d5-8f99-4068-afc0-19594fb42f48'."
          // Technically means that GET `/organizations/current/users/${userId}` was 404 and no `data` returned
          // But it's OK for us - user exists, but not in this organization, so allow to invite such user
          setFormState({ helperInfo: "", pseudoError: false });
          setContextDisableSubmit(false);
        } else if (
          dataFromUserByOrg?.organizationRole === OrganizationRole.User
        ) {
          // Use case - attempt to re-invite existed user
          setFormState({
            helperInfo: infoWhenUserIsOrganizationUser,
            pseudoError: true
          });
          setContextDisableSubmit(true);
        } else if (
          dataFromUserByOrg?.organizationRole === OrganizationRole.Admin
        ) {
          // Use Case - attempt to re-invite existed user
          setFormState({
            helperInfo: infoWhenUserIsOrganizationAdmin,
            pseudoError: true
          });
          setContextDisableSubmit(true);
        }

        break;

      default:
        break;
    }

    // When no data from first endpoint above
    // And it's OK - it is existing user - email address is not used - Allow
    if (errorFromUserByEmail) {
      setFormState({ helperInfo: "", pseudoError: false });

      // We need to clear context value so that it not remain input for accidental usage
      setContextDisplayName("");
      // UPDATE. 404 is also for user by email who is already invited but within another organization.
      // Maybe we SHOULD NOT clear display name

      // If all good, we need to be sure Submit can happen
      setContextDisableSubmit(false);
    }

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataFromUserByEmail,
    errorFromUserByEmail,
    dataFromUserByOrg,
    errorFromUserByOrg
  ]);

  const changeHandler = event => {
    setFormState({
      helperInfo: "",
      pseudoError: false
    });

    const val = event.target.value.replaceAll(" ", "");

    const { value: fastValue, isValid: fastIsValid } = valueChangeHandler(val);

    setEmail(fastValue);

    if (onEmailChange) {
      onEmailChange(fastValue, fastIsValid);
    }
  };

  const isLoading =
    loadingDataFromUserByEmail || loadingDataFromCurrentOrganization;

  const hasError = () => {
    if (formState.pseudoError) {
      // No matter if GET /users/{email} is OK (200, 201, 404) or /organizations/{organizationId}/users/{userId}
      // We need to use red validation message placeholder beneath the Email field
      return true;
    }

    // Default UI validation logic
    return !isValid;
  };

  const modifyHelperText = () => {
    // Default UI validation logic
    return isValid ? undefined : errorMessage;
  };

  return (
    <Input
      type="email"
      id={id}
      data-testid={id}
      label={label}
      error={hasError()}
      helperText={formState.helperInfo || modifyHelperText()}
      value={email}
      endInputAdornment={<EmailInputAdornment isLoading={isLoading} />}
      onBlur={changeHandler}
      onChange={changeHandler}
    />
  );
};
